import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { updateUserActivationStatus, clearUser } from '../../userSlice';
import { clearAllChats } from '../../chatSlice';
import { getMe } from '../utils/chatUtils';

interface UsePeriodicActivationCheckOptions {
  intervalMs?: number; // Интервал проверки в миллисекундах
  enabled?: boolean; // Включена ли периодическая проверка
}

export const usePeriodicActivationCheck = (options: UsePeriodicActivationCheckOptions = {}) => {
  const { intervalMs = 30000, enabled = true } = options; // По умолчанию каждые 30 секунд

  const dispatch: AppDispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkActivationStatus = async () => {
    if (!user) return;

    try {
      const profile = await getMe();

      if (profile) {
        // Обновляем статус активации в Redux
        dispatch(updateUserActivationStatus({ is_active: profile.is_active }));
      }
    } catch (error) {
      // Если ошибка авторизации, очищаем пользователя и кеш чатов
      dispatch(clearUser());
      dispatch(clearAllChats());
    }
  };

  // Запускаем периодическую проверку
  useEffect(() => {
    if (!enabled || !user) {
      return;
    }

    // Первая проверка сразу
    checkActivationStatus();

    // Устанавливаем интервал
    intervalRef.current = setInterval(checkActivationStatus, intervalMs);

    // Очистка при размонтировании
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user?.id, enabled, intervalMs]);

  // Останавливаем интервал при отключении
  useEffect(() => {
    if (!enabled && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [enabled]);

  return {
    checkActivationStatus
  };
};
